import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Fade, Paper, Popper, useTheme } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridPinnedRowsProp,
  GridSortDirection,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { InfoOutlined } from '@mui/icons-material';
import { EnhancedTableToolbar } from '../../../components/EnhancedTableToolbar';
import IconMenu, { IMenuItem } from '../../../components/IconMenu';
import ToolButton from '../../../components/ToolButton';
import { SettingsContext } from '../../../context/SettingsContext';
import { TroncFiltersContext } from '../../../context/TroncFiltersContext';
import TroncReportFiltersPanel from './TroncReportFiltersPanel';
import { UIContext } from '../../../context/UIContext';
import { defer, formatValue } from '../../../helpers/helpers';
import { ExportDialog } from './ExportDialog';
import FailedToLoadTronc from '../../../components/FailedToLoad';
import Loading from '../../../components/Loading';
import { troncHash, TroncRow, TroncSummary } from '../../../models/tronc';
import { UserContext } from '../../../context/UserContext';
import { setupArrayAtPolyfill } from '../../../polyfills';
import EmptyResult from '../../../components/EmptyResult';

const FileDownloadIcon =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' style='width:24px;height:24px' viewBox='0 0 24 24'%3E%3Cpath fill='rgb(89,89,89)' d='M14,2L20,8V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2H14M18,20V9H13V4H6V20H18M12,19L8,15H10.5V12H13.5V15H16L12,19Z' /%3E%3C/svg%3E";
const FileDownloadIconWhite =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' style='width:24px;height:24px' viewBox='0 0 24 24'%3E%3Cpath fill='rgb(178,178,178)' d='M14,2L20,8V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2H14M18,20V9H13V4H6V20H18M12,19L8,15H10.5V12H13.5V15H16L12,19Z' /%3E%3C/svg%3E";

const DEFAULT_ROWS_PER_PAGE = 10;
const ROW_SIZE = 52;

interface TroncTableProps {
  rows: TroncRow[];
  summary: TroncSummary;
  currency: string;
  isLoading: boolean;
  isError: boolean;
  page: number;
  setPage: (p: number) => void;
  refresh: () => void;
}

const getSummaryRow = (
  summary: TroncSummary,
  rowLabel: string
): GridValidRowModel[] =>
  [{
    id: 'summary',
    staffName: rowLabel,
    hours: summary?.totalHours,
    gratuity: summary?.totalGratuity,
    serviceCharge: summary?.totalServiceCharge,
    totalTronc: summary?.totalTronc
  }];

const HOURS_KEY = 'hourLegend';
const GRATUITY_KEY = 'gratuityLegend';
const SERVICE_CHANGE_KEY = 'serviceChargeLegend'

const TroncTable = (props: TroncTableProps) => {
  const { rows, summary, currency, isLoading, isError, refresh, page, setPage } = props;
  const { locale } = useContext(UserContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const filtersButtonRef = useRef(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | undefined>(undefined);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [exportReportStarted, setExportReportStarted] = useState(false);

  const { settings } = useContext(SettingsContext);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState('asc' as GridSortDirection);
  const [orderBy, setOrderBy] = useState('date');
  const { filtersOpen, openFilters, closeFilters, chips, isLoading: isLoadingFiltersContext } = useContext(TroncFiltersContext);
  const [summaryRow, setSummaryRow] = useState<GridPinnedRowsProp>({});
  const [rowNumberCurrentlyShowing, setRowNumberCurrentlyShowing] = useState(0);

  const { colorMode } = useContext(UIContext);

  const [currentKey, setCurrentKey] = useState(HOURS_KEY);
  const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);

  const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
    // Stop propagation to prevent sorting when clicking on info icon
    event.stopPropagation();
    setCurrentKey(event.currentTarget.id);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'hours-popover' : undefined;

  // Support old browsers
  useEffect(setupArrayAtPolyfill, []);

  const toggleFilters = useCallback(() =>
    defer(() => {
      if (filtersOpen) {
        closeFilters(true);
      } else {
        openFilters();
      }
    }), [filtersOpen, closeFilters, openFilters]
  );

  useEffect(() => {
    setSummaryRow({ bottom: getSummaryRow(summary, t('screens.transactions.fields.total')) });
  }, [summary, t]);

  useEffect(() => {
    if (rows.length < rowsPerPage) setRowNumberCurrentlyShowing(rows.length);
    else setRowNumberCurrentlyShowing(rowsPerPage);
  }, [rowsPerPage, page, rows])

  const defaultColumns: GridColDef[] = [
    {
      field: 'staffName',
      headerName: t('screens.troncReport.fields.staffName'),
      minWidth: 200,
      display: 'flex',
      flex: 1,
    },
    {
      field: 'hours',
      headerName: t('screens.troncReport.fields.hours'),
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      display: 'flex',
      flex: 1,
      valueGetter: (value, row: TroncRow) => (row.hours ?? 0).toFixed(2),
      renderHeader: (params) => (
        <>
          {params.colDef.headerName}
          <InfoOutlined
            id={HOURS_KEY}
            onClick={handleClick}
            sx={{ color: currentKey === HOURS_KEY && open ? theme?.palette?.info['160p'] : theme?.palette?.grey?.['54p'],
              marginLeft: '4px', cursor: 'pointer', fontSize: '1rem', verticalAlign: 'middle' }} />
        </>
      ),
    },
    {
      field: 'gratuity',
      headerName: t('screens.troncReport.fields.gratuity'),
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      display: 'flex',
      flex: 1,
      valueGetter: (value, row: TroncRow) =>
        formatValue(row.gratuity, true, currency, locale),
      renderHeader: (params) => (
        <>
          {params.colDef.headerName}
          <InfoOutlined
            id={GRATUITY_KEY}
            onClick={handleClick}
            sx={{ color: currentKey === GRATUITY_KEY && open ? theme?.palette?.info['160p'] : theme?.palette?.grey?.['54p'],
              marginLeft: '4px', cursor: 'pointer', fontSize: '1rem', verticalAlign: 'middle' }} />
        </>
      ),
    },
    {
      field: 'serviceCharge',
      headerName: t('screens.troncReport.fields.serviceCharge'),
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      display: 'flex',
      flex: 1,
      valueGetter: (value, row: TroncRow) =>
        formatValue(row.serviceCharge, true, currency, locale),
      renderHeader: (params) => (
        <>
          {params.colDef.headerName}
          <InfoOutlined
            id={SERVICE_CHANGE_KEY}
            onClick={handleClick}
            sx={{ color: currentKey === SERVICE_CHANGE_KEY && open ? theme?.palette?.info['160p'] : theme?.palette?.grey?.['54p'],
              marginLeft: '4px', cursor: 'pointer', fontSize: '1rem', verticalAlign: 'middle' }} />
        </>
      ),
    },
    {
      field: 'total',
      headerName: t('screens.troncReport.fields.totalAmount'),
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      display: 'flex',
      flex: 1,
      valueGetter: (value, row: TroncRow) =>
        formatValue(row.totalTronc, true, currency, locale)
    }
  ]

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    if (sortModel?.length) {
      setOrder(sortModel[0].sort);
      setOrderBy(sortModel[0].field);
    }
  }, [setOrder, setOrderBy]
  );

  const handlePaginationModelChange = useCallback((paginationModel: GridPaginationModel) => {
    setPage(paginationModel.page);
    setRowsPerPage(paginationModel.pageSize);
  }, [setPage, setRowsPerPage]
  );

  const toolbarChips = useMemo(() =>
    chips.map((chip) => ({
      ...chip,
      onClick: chip.onClick ? chip.onClick : toggleFilters,
    })), [chips, toggleFilters]
  );

  const openExportMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setExportMenuAnchor(event.currentTarget);
    setIsExportMenuOpen((prevOpen) => !prevOpen);
  }, [setExportMenuAnchor, setIsExportMenuOpen]
  );

  const exportOnComplete = useCallback(() => {
    setExportReportStarted(false);
  }, [setExportReportStarted]
  );

  const exportFilteredTronc = useCallback(() => {
    if (exportReportStarted) {
      return;
    }
    setExportReportStarted(true);
  }, [exportReportStarted, setExportReportStarted]
  );

  const exportMenuItems = useMemo(() =>
    [{
      dataQaId: "exportMenuTroncCSV",
      title: t('export.troncReport'),
      onClick: () => { exportFilteredTronc() },
      icon: (
        <img
          src={colorMode === 'light' ? FileDownloadIcon : FileDownloadIconWhite}
          style={{ width: '22px', height: '22px' }}
          alt={t('export.troncReport')}
        />),
      iconPosition: 'right',
      rightText: '.csv',
    }] as IMenuItem[],
    [t, exportFilteredTronc, colorMode]
  );

  const getExportRows = () => (
    [...rows,
    {
      requestStartTime: '',
      requestEndTime: '',
      staffName: '',
      staffId: '',
      locationId: '',
      locationName: 'Total',
      hours: summary.totalHours,
      gratuity: summary.totalGratuity,
      serviceCharge: summary.totalServiceCharge,
      totalTronc: summary.totalTronc,
      noOfShiftRecords: 0
    }]
  );

  return (
    <Box id="box-container"
      sx={{
        width: '100%',
        flexGrow: 1,
        height: (!rows?.length || isError || isLoading) ? '100%' : 'unset'
      }}
    >
      <Paper
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexFlow: 'column'
        }}
        variant="outlined">
        {!isLoadingFiltersContext && <EnhancedTableToolbar
          onFilterClick={toggleFilters}
          chips={toolbarChips}
          isFilterOpen={filtersOpen}
          filtersButtonRef={filtersButtonRef}
          extraButtons={
            (!isError && !isLoading && rows.length > 0) ? (
              <Box sx={{ marginRight: { xs: 0, md: '1.5em' } }}>
                <ToolButton
                  iconElement={<FileDownloadOutlinedIcon />}
                  tooltip={t('actions.export')}
                  onClick={(ev) => openExportMenu(ev)}
                  text={t('actions.export')}
                  active={isExportMenuOpen}
                  id="exportButton"
                  buttonStyle={{ marginRight: '16px' }}
                  dataQaId="tableExportButton"
                />
              </Box>
            ) : undefined
          }
          tableHasTransactions={!isError && !isLoading && rows.length > 0}
        />}
        <ExportDialog
          start={exportReportStarted}
          onComplete={exportOnComplete}
          rows={getExportRows()}
        />
        <IconMenu
          items={exportMenuItems}
          open={isExportMenuOpen}
          onClose={() => { setIsExportMenuOpen(false) }}
          ariaLabelledBy="exportButton"
          anchorEl={exportMenuAnchor}
          menuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
            transformOrigin: { vertical: 'top', horizontal: 'center' },
            sx: { marginTop: '16px' },
          }}
        />
        <Box sx={{
          flex: 1,
          position: 'relative', width: '100%', height: '100%', minHeight: `${ROW_SIZE * (rowNumberCurrentlyShowing + 2)}px`
        }} >
          <Box sx={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            inset: 0,
          }}>
            {isLoading && <Loading />}
            {!isLoading && isError && (
              <FailedToLoadTronc onClickRetry={() => defer(refresh)} />
            )}
            {rows.length === 0 && !isLoading && !isError && <EmptyResult title={t('components.iconMessage.noTronc.title')} subtitle={t('components.iconMessage.noTronc.subtitle')} />}
            {!isLoading && !isError && rows.length > 0 && (
              <DataGridPro
                autoHeight
                columns={defaultColumns}
                columnHeaderHeight={55}
                disableColumnMenu
                getRowHeight={() => 'auto'}
                rows={rows}
                getRowId={(row: TroncRow) => troncHash(row)}
                loading={isLoading}
                disableMultipleRowSelection
                onSortModelChange={handleSortModelChange}
                pagination
                pageSizeOptions={[10, 25, 50, 100]}
                onPaginationModelChange={handlePaginationModelChange}
                rowCount={rows?.length || 0}
                initialState={{
                  pagination: { paginationModel: { page, pageSize: rowsPerPage } },
                  sorting: { sortModel: [{ field: orderBy, sort: order }] },
                }}
                localeText={{
                  MuiTablePagination: {
                    labelRowsPerPage: t('components.filters.rowsPerPage')
                  },
                  footerRowSelected: () => ''
                }}
                pinnedRows={summaryRow as GridPinnedRowsProp<TroncRow>}
                sx={{
                  position: 'absolute',
                  height: '100%',
                  width: '100%',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: theme.palette.secondary.main,
                    transition: 'background-color 0.2s ease',
                  },
                  '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
                    outline: 'none',
                  },

                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-columnHeader, .MuiDataGrid-scrollbarFiller, .MuiDataGrid-filler': {
                    backgroundColor: 'background.paper'
                  },
                  '& ::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px'
                  },
                  '& ::-webkit-scrollbar-thumb': {
                    borderRadius: '10px',
                    backgroundColor: theme?.palette?.grey?.['50p']
                  },
                  '& .MuiDataGrid-cell': {
                    minHeight: '52px',
                    paddingTop: '16px',
                    paddingBottom: '16px'
                  },
                  '& .MuiDataGrid-container--top [role=row], .MuiDataGrid-container--bottom [role=row]': {
                    fontWeight: 600,
                    backgroundColor: 'background.paper'
                  }
                }}
              />
            )}
          </Box>
        </Box>
        <Popper open={filtersOpen} anchorEl={filtersButtonRef.current} placement="bottom-end">
          <Fade mountOnEnter unmountOnExit in={filtersOpen}>
            <Paper
              sx={{
                width: '450px',
                height: rows?.length ? `${ROW_SIZE * (rowNumberCurrentlyShowing + 2)}px` : 'calc(100vh - 315px)',
                minHeight: `calc(100vh - 320px)`,
                marginTop: { xs: '5px', sm: '9px', md: '7px' },
                position: 'fixed',
                right: '-16px',
                top: 0,
                overflow: 'auto',
                borderRadius: '0px',
                boxShadow:
                  '0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)',
                clipPath: 'inset(0px 0px 0px -74px)',
                backgroundColor: 'background.paper',
                backgroundImage: 'none',
              }}
            >
              <TroncReportFiltersPanel />
            </Paper>
          </Fade>
        </Popper>
      </Paper>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: {
              marginTop: "2px",
              backgroundColor: theme?.palette?.info?.['160p'],
              color: 'white',
              maxWidth: '300px',
            },
          },
        }}
      >
        <Typography sx={{ p: 2 }}>{t(`screens.troncReport.fields.${currentKey}`)}</Typography>
      </Popover>
    </Box>
  )
}


export default TroncTable;
